/**
 * Centralized WebSocket Manager
 * 
 * This singleton manages a single socket.io connection for the entire application.
 * All components should use this manager instead of creating their own sockets.
 * 
 * Usage:
 * - socketManager.connect({ token, userId, company }) - connect with auth
 * - socketManager.disconnect() - disconnect and cleanup
 * - socketManager.subscribe(event, callback) - subscribe to events (returns unsubscribe fn)
 * - socketManager.emit(event, payload) - emit events to server
 * - useSocketSubscription(event, callback, deps) - React hook for subscriptions
 */

import { io, Socket } from 'socket.io-client';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { getSocketUrl } from '../utils/socketUrl';

type EventCallback = (...args: any[]) => void;

interface AuthConfig {
  token: string;
  userId: string;
  company: string;
}

interface SocketManagerInterface {
  connect: (auth: AuthConfig) => void;
  disconnect: () => void;
  isConnected: () => boolean;
  subscribe: (event: string, callback: EventCallback) => () => void;
  unsubscribe: (event: string, callback: EventCallback) => void;
  emit: (event: string, payload?: any) => void;
  getSocket: () => Socket | null;
  onConnectionChange: (callback: (connected: boolean) => void) => () => void;
}

class WebSocketManager implements SocketManagerInterface {
  private socket: Socket | null = null;
  private subscribers: Map<string, Set<EventCallback>> = new Map();
  private connectionListeners: Set<(connected: boolean) => void> = new Set();
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private auth: AuthConfig | null = null;
  private isConnecting: boolean = false;

  /**
   * Connect to the WebSocket server
   */
  connect(auth: AuthConfig): void {
    // CRITICAL: Prevent duplicate connections
    if (this.socket?.connected) {
      return;
    }

    // CRITICAL: Prevent concurrent connection attempts
    if (this.isConnecting) {
      return;
    }

    // Disconnect existing socket if any (but only if it's actually connected or connecting)
    if (this.socket && (this.socket.connected || this.isConnecting)) {
      this.cleanupSocket();
    }

    this.auth = auth;
    this.isConnecting = true;
    const baseUrl = getSocketUrl();

    this.socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      auth: {
        token: auth.token,
        userId: auth.userId,
        company: auth.company,
      },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: true,
    });

    this.setupCoreListeners();
    this.isConnecting = false;
  }

  /**
   * Setup core socket event listeners
   */
  private setupCoreListeners(): void {
    if (!this.socket) return;

    // CRITICAL: Remove existing listeners to prevent duplicates
    this.socket.removeAllListeners();

    this.socket.on('connect', () => {
      // Join user and company rooms
      if (this.auth) {
        const rooms = [
          this.auth.userId,
          `user:${this.auth.userId}`,
          `company:${this.auth.company}`,
        ];
        
        rooms.forEach((room) => {
          this.socket?.emit('join', room);
        });
      }

      // Start heartbeat
      this.startHeartbeat();

      // Notify connection listeners
      this.notifyConnectionChange(true);
    });

    this.socket.on('disconnect', (reason) => {
      this.stopHeartbeat();
      this.notifyConnectionChange(false);
    });

    this.socket.on('connect_error', (err) => {
      // Connection error - silently handle
    });

    this.socket.on('reconnect', (attemptNumber) => {
      // Re-join rooms after reconnect
      if (this.auth) {
        const rooms = [
          this.auth.userId,
          `user:${this.auth.userId}`,
          `company:${this.auth.company}`,
        ];
        rooms.forEach((room) => {
          this.socket?.emit('join', room);
        });
      }

      // Restart heartbeat
      this.startHeartbeat();
      this.notifyConnectionChange(true);
    });

    this.socket.on('reconnect_error', (err) => {
      // Reconnection error - silently handle
    });

    // Proxy ALL incoming events to subscribers using onAny
    this.socket.onAny((event: string, ...args: any[]) => {
      this.dispatchToSubscribers(event, ...args);
    });
  }

  /**
   * Dispatch event to all registered subscribers
   * Uses microtask queue to batch callbacks and prevent [Violation] warnings
   * This defers execution to the next event loop tick, preventing blocking
   */
  private dispatchToSubscribers(event: string, ...args: any[]): void {
    const callbacks = this.subscribers.get(event);
    if (callbacks && callbacks.size > 0) {
      // Use Promise.resolve() to defer to microtask queue
      // This batches callbacks and prevents [Violation] 'message' handler warnings
      // while still executing quickly (faster than setTimeout)
      Promise.resolve().then(() => {
        callbacks.forEach((callback) => {
          try {
            callback(...args);
          } catch (err) {
            // Error in event handler - silently handle
          }
        });
      });
    }
  }

  /**
   * Start presence heartbeat interval
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    // Send initial heartbeat
    if (this.socket?.connected) {
      this.socket.emit('presence:heartbeat', { device: 'web' });
    }

    // Set up interval for heartbeat every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('presence:heartbeat', { device: 'web' });
      }
    }, 30000);
  }

  /**
   * Stop presence heartbeat interval
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Notify all connection state listeners
   */
  private notifyConnectionChange(connected: boolean): void {
    this.connectionListeners.forEach((listener) => {
      try {
        listener(connected);
      } catch (err) {
        // Error in connection listener - silently handle
      }
    });
  }

  /**
   * Cleanup socket and all listeners
   */
  private cleanupSocket(): void {
    this.stopHeartbeat();
    
    if (this.socket) {
      // Only disconnect if socket is actually connected
      // Prevents "WebSocket is closed before the connection is established" errors
      const isConnected = this.socket.connected;
      
      this.socket.offAny();
      this.socket.removeAllListeners();
      
      // Only call disconnect if socket is connected
      // If it's still connecting, removing listeners is enough
      if (isConnected) {
        this.socket.disconnect();
      }
      
      this.socket = null;
    }
    
    this.isConnecting = false;
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.cleanupSocket();
    this.subscribers.clear();
    this.connectionListeners.clear();
    this.auth = null;
    this.notifyConnectionChange(false);
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Subscribe to a socket event
   * @returns Unsubscribe function
   */
  subscribe(event: string, callback: EventCallback): () => void {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, new Set());
    }
    
    this.subscribers.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.unsubscribe(event, callback);
    };
  }

  /**
   * Unsubscribe from a socket event
   */
  unsubscribe(event: string, callback: EventCallback): void {
    const callbacks = this.subscribers.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.subscribers.delete(event);
      }
    }
  }

  /**
   * Emit an event to the server
   */
  emit(event: string, payload?: any): void {
    if (!this.socket?.connected) {
      return;
    }
    this.socket.emit(event, payload);
  }

  /**
   * Get the underlying socket instance (use sparingly)
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Register a connection state change listener
   * @returns Unsubscribe function
   */
  onConnectionChange(callback: (connected: boolean) => void): () => void {
    this.connectionListeners.add(callback);
    
    // Immediately notify current state
    callback(this.isConnected());

    return () => {
      this.connectionListeners.delete(callback);
    };
  }

  /**
   * Get current auth info
   */
  getAuth(): AuthConfig | null {
    return this.auth;
  }
}

// Create singleton instance
export const socketManager = new WebSocketManager();

// ============================================================================
// React Hooks
// ============================================================================

/**
 * Hook to subscribe to a single socket event
 * 
 * @param event - Event name to subscribe to
 * @param callback - Callback function when event is received
 * @param deps - Dependency array (optional, callback will use latest ref)
 * 
 * @example
 * ```tsx
 * useSocketSubscription('notification', (payload) => {
 *   setNotifications(prev => [payload, ...prev]);
 * });
 * ```
 */
export function useSocketSubscription(
  event: string,
  callback: EventCallback,
  deps: React.DependencyList = []
): void {
  const callbackRef = useRef(callback);
  
  // Always keep the latest callback
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const handler: EventCallback = (...args) => {
      callbackRef.current(...args);
    };

    const unsubscribe = socketManager.subscribe(event, handler);
    
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, ...deps]);
}

/**
 * Hook to subscribe to multiple socket events
 * 
 * @param subscriptions - Object mapping event names to callbacks
 * @param deps - Dependency array (optional)
 * 
 * @example
 * ```tsx
 * useSocketSubscriptions({
 *   'notification': (payload) => handleNotification(payload),
 *   'notification-deleted': (id) => handleDelete(id),
 *   'notification-updated': (n) => handleUpdate(n),
 * });
 * ```
 */
export function useSocketSubscriptions(
  subscriptions: Record<string, EventCallback>,
  deps: React.DependencyList = []
): void {
  const subsRef = useRef(subscriptions);
  
  // Keep latest subscriptions reference
  useEffect(() => {
    subsRef.current = subscriptions;
  }, [subscriptions]);

  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    Object.entries(subsRef.current).forEach(([event, callback]) => {
      const handler: EventCallback = (...args) => {
        subsRef.current[event]?.(...args);
      };
      
      const unsub = socketManager.subscribe(event, handler);
      unsubscribers.push(unsub);
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Hook to emit socket events
 * Returns a stable emit function
 */
export function useSocketEmit(): (event: string, payload?: any) => void {
  return useCallback((event: string, payload?: any) => {
    socketManager.emit(event, payload);
  }, []);
}

/**
 * Hook to track socket connection state
 */
export function useSocketConnection(): boolean {
  const [isConnected, setIsConnected] = React.useState(socketManager.isConnected());

  useEffect(() => {
    const unsubscribe = socketManager.onConnectionChange(setIsConnected);
    return unsubscribe;
  }, []);

  return isConnected;
}